import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useToast } from '@/hooks/use-toast';
import { arrayMove } from '@dnd-kit/sortable';
import { DragEndEvent } from '@dnd-kit/core';
import { Category, TicketTag, COLOR_OPTIONS } from '@/components/layout/SidebarDialogs';

interface Agent {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  ticket_count?: number;
}

interface SidebarCounts {
  total_tickets: number;
  unassigned_count: number;
  category_counts: Record<string, number>;
  tag_counts: Record<string, number>;
  agent_counts: Record<string, number>;
}

export function useSecondarySidebarData() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeWorkspace } = useWorkspaceContext();
  const { toast } = useToast();

  // Data state
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<TicketTag[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [totalTickets, setTotalTickets] = useState(0);
  const [loading, setLoading] = useState(true);

  // Category dialog state
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryColor, setCategoryColor] = useState(COLOR_OPTIONS[0]);
  const [savingCategory, setSavingCategory] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  // Tag dialog state
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [editingTag, setEditingTag] = useState<TicketTag | null>(null);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState(COLOR_OPTIONS[4]);
  const [savingTag, setSavingTag] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<TicketTag | null>(null);

  // Current filters from URL
  const currentCategoryId = searchParams.get('category');
  const currentTagId = searchParams.get('tag');
  const currentAgentId = searchParams.get('agent');

  // Fetch data
  useEffect(() => {
    if (!activeWorkspace?.id) return;

    const fetchData = async () => {
      setLoading(true);

      const { data: countsData } = await supabase.rpc('get_sidebar_counts', {
        p_workspace_id: activeWorkspace.id
      });

      const counts = countsData as unknown as SidebarCounts | null;

      if (counts) {
        setTotalTickets(counts.total_tickets || 0);
        setUnassignedCount(counts.unassigned_count || 0);
      }

      const { data: categoriesData } = await supabase
        .from('ticket_categories')
        .select('id, name, color, position')
        .eq('workspace_id', activeWorkspace.id)
        .order('position', { ascending: true });

      if (categoriesData && counts) {
        const categoriesWithCounts = categoriesData.map(cat => ({
          ...cat,
          ticket_count: counts.category_counts?.[cat.id] || 0
        }));
        setCategories(categoriesWithCounts);
      }

      const { data: tagsData } = await supabase
        .from('ticket_tags')
        .select('id, name, color, position')
        .eq('workspace_id', activeWorkspace.id)
        .order('position', { ascending: true });

      if (tagsData && counts) {
        const tagsWithCounts = tagsData.map(tag => ({
          ...tag,
          ticket_count: counts.tag_counts?.[tag.id] || 0
        }));
        setTags(tagsWithCounts);
      }

      const { data: membersData } = await supabase
        .from('workspace_members')
        .select(`
          id,
          user_id,
          profile:profiles(first_name, last_name, avatar_url)
        `)
        .eq('workspace_id', activeWorkspace.id);

      if (membersData && counts) {
        const agentsWithCounts = membersData.map(member => {
          const profile = member.profile as { first_name: string | null; last_name: string | null; avatar_url: string | null } | null;
          return {
            id: member.id,
            user_id: member.user_id,
            first_name: profile?.first_name || null,
            last_name: profile?.last_name || null,
            avatar_url: profile?.avatar_url || null,
            ticket_count: counts.agent_counts?.[member.user_id] || 0,
          };
        });
        setAgents(agentsWithCounts);
      }

      setLoading(false);
    };

    fetchData();
  }, [activeWorkspace?.id]);

  // Category operations
  const openNewCategoryDialog = useCallback(() => {
    setEditingCategory(null);
    setCategoryName('');
    setCategoryColor(COLOR_OPTIONS[Math.floor(Math.random() * COLOR_OPTIONS.length)]);
    setShowCategoryDialog(true);
  }, []);

  const openEditCategoryDialog = useCallback((category: Category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setCategoryColor(category.color);
    setShowCategoryDialog(true);
  }, []);

  const saveCategory = useCallback(async () => {
    if (!categoryName.trim() || !activeWorkspace?.id) return;

    setSavingCategory(true);

    if (editingCategory) {
      const { error } = await supabase
        .from('ticket_categories')
        .update({ name: categoryName.trim(), color: categoryColor })
        .eq('id', editingCategory.id);

      if (error) {
        toast({ title: 'Erreur', description: 'Impossible de modifier le dossier', variant: 'destructive' });
      } else {
        setCategories(prev => prev.map(c =>
          c.id === editingCategory.id
            ? { ...c, name: categoryName.trim(), color: categoryColor }
            : c
        ));
        toast({ title: 'Dossier modifié' });
      }
    } else {
      const { data, error } = await supabase
        .from('ticket_categories')
        .insert({
          workspace_id: activeWorkspace.id,
          name: categoryName.trim(),
          color: categoryColor,
          position: categories.length,
        })
        .select()
        .single();

      if (error) {
        toast({ title: 'Erreur', description: 'Impossible de créer le dossier', variant: 'destructive' });
      } else {
        setCategories(prev => [...prev, { ...data, ticket_count: 0 }]);
        toast({ title: 'Dossier créé' });
      }
    }

    setSavingCategory(false);
    setShowCategoryDialog(false);
  }, [categoryName, categoryColor, editingCategory, activeWorkspace?.id, categories.length, toast]);

  const deleteCategory = useCallback(async () => {
    if (!categoryToDelete) return;

    const { error } = await supabase
      .from('ticket_categories')
      .delete()
      .eq('id', categoryToDelete.id);

    if (error) {
      toast({ title: 'Erreur', description: 'Impossible de supprimer le dossier', variant: 'destructive' });
    } else {
      setCategories(prev => prev.filter(c => c.id !== categoryToDelete.id));
      toast({ title: 'Dossier supprimé' });

      if (currentCategoryId === categoryToDelete.id) {
        navigate('/tickets');
      }
    }

    setCategoryToDelete(null);
  }, [categoryToDelete, currentCategoryId, navigate, toast]);

  // Tag operations
  const openNewTagDialog = useCallback(() => {
    setEditingTag(null);
    setTagName('');
    setTagColor(COLOR_OPTIONS[Math.floor(Math.random() * COLOR_OPTIONS.length)]);
    setShowTagDialog(true);
  }, []);

  const openEditTagDialog = useCallback((tag: TicketTag) => {
    setEditingTag(tag);
    setTagName(tag.name);
    setTagColor(tag.color);
    setShowTagDialog(true);
  }, []);

  const saveTag = useCallback(async () => {
    if (!tagName.trim() || !activeWorkspace?.id) return;

    setSavingTag(true);

    if (editingTag) {
      const { error } = await supabase
        .from('ticket_tags')
        .update({ name: tagName.trim(), color: tagColor })
        .eq('id', editingTag.id);

      if (error) {
        toast({ title: 'Erreur', description: 'Impossible de modifier le tag', variant: 'destructive' });
      } else {
        setTags(prev => prev.map(t =>
          t.id === editingTag.id
            ? { ...t, name: tagName.trim(), color: tagColor }
            : t
        ));
        toast({ title: 'Tag modifié' });
      }
    } else {
      const { data, error } = await supabase
        .from('ticket_tags')
        .insert({
          workspace_id: activeWorkspace.id,
          name: tagName.trim(),
          color: tagColor,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          toast({ title: 'Erreur', description: 'Ce tag existe déjà', variant: 'destructive' });
        } else {
          toast({ title: 'Erreur', description: 'Impossible de créer le tag', variant: 'destructive' });
        }
      } else {
        setTags(prev => [...prev, { ...data, ticket_count: 0 }]);
        toast({ title: 'Tag créé' });
      }
    }

    setSavingTag(false);
    setShowTagDialog(false);
  }, [tagName, tagColor, editingTag, activeWorkspace?.id, toast]);

  const deleteTag = useCallback(async () => {
    if (!tagToDelete) return;

    const { error } = await supabase
      .from('ticket_tags')
      .delete()
      .eq('id', tagToDelete.id);

    if (error) {
      toast({ title: 'Erreur', description: 'Impossible de supprimer le tag', variant: 'destructive' });
    } else {
      setTags(prev => prev.filter(t => t.id !== tagToDelete.id));
      toast({ title: 'Tag supprimé' });

      if (currentTagId === tagToDelete.id) {
        navigate('/tickets');
      }
    }

    setTagToDelete(null);
  }, [tagToDelete, currentTagId, navigate, toast]);

  // Navigation handlers
  const handleCategoryClick = useCallback((categoryId: string | null) => {
    if (categoryId) {
      navigate(`/tickets?category=${categoryId}`);
    } else {
      navigate('/tickets');
    }
  }, [navigate]);

  const handleTagClick = useCallback((tagId: string) => {
    navigate(`/tickets?tag=${tagId}`);
  }, [navigate]);

  const handleAgentClick = useCallback((agentId: string | null) => {
    if (agentId === 'unassigned') {
      navigate('/tickets?agent=unassigned');
    } else if (agentId) {
      navigate(`/tickets?agent=${agentId}`);
    } else {
      navigate('/tickets');
    }
  }, [navigate]);

  // Drag handlers
  const handleCategoryDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((cat) => cat.id === active.id);
      const newIndex = categories.findIndex((cat) => cat.id === over.id);

      const newCategories = arrayMove(categories, oldIndex, newIndex);
      setCategories(newCategories);

      await Promise.all(
        newCategories.map((cat, index) =>
          supabase
            .from('ticket_categories')
            .update({ position: index })
            .eq('id', cat.id)
        )
      );

      toast({ title: 'Ordre mis à jour' });
    }
  }, [categories, toast]);

  const handleTagDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = tags.findIndex((tag) => tag.id === active.id);
      const newIndex = tags.findIndex((tag) => tag.id === over.id);

      const newTags = arrayMove(tags, oldIndex, newIndex);
      setTags(newTags);

      await Promise.all(
        newTags.map((tag, index) =>
          supabase
            .from('ticket_tags')
            .update({ position: index })
            .eq('id', tag.id)
        )
      );

      toast({ title: 'Ordre mis à jour' });
    }
  }, [tags, toast]);

  return {
    // Data
    categories,
    tags,
    agents,
    totalTickets,
    unassignedCount,
    loading,
    
    // Current filters
    currentCategoryId,
    currentTagId,
    currentAgentId,
    
    // Category dialog
    showCategoryDialog,
    setShowCategoryDialog,
    editingCategory,
    categoryName,
    setCategoryName,
    categoryColor,
    setCategoryColor,
    savingCategory,
    categoryToDelete,
    setCategoryToDelete,
    openNewCategoryDialog,
    openEditCategoryDialog,
    saveCategory,
    deleteCategory,
    
    // Tag dialog
    showTagDialog,
    setShowTagDialog,
    editingTag,
    tagName,
    setTagName,
    tagColor,
    setTagColor,
    savingTag,
    tagToDelete,
    setTagToDelete,
    openNewTagDialog,
    openEditTagDialog,
    saveTag,
    deleteTag,
    
    // Navigation
    handleCategoryClick,
    handleTagClick,
    handleAgentClick,
    
    // Drag handlers
    handleCategoryDragEnd,
    handleTagDragEnd,
  };
}

export type { Agent };
